import * as React from 'react';
import { AgGridReact } from '@ag-grid-community/react';
import AdaptableReactComponent from './AdaptableReact';
import { ComponentUtil } from '@ag-grid-community/core';
import { diff } from '@adaptabletools/adaptable/src/Utilities/Extensions/ObjectExtensions';
var AdaptableAgGridStateTransitions;
(function (AdaptableAgGridStateTransitions) {
    AdaptableAgGridStateTransitions["AG_GRID_EMIT_PROPS"] = "AG_GRID_EMIT_PROPS";
    AdaptableAgGridStateTransitions["INITIALIZE_ADAPTABLE"] = "INITIALIZE_ADAPTABLE";
    AdaptableAgGridStateTransitions["INITIALIZE_AG_GRID"] = "INITIALIZE_AG_GRID";
})(AdaptableAgGridStateTransitions || (AdaptableAgGridStateTransitions = {}));
const agGridPropertySet = new Set(ComponentUtil.ALL_PROPERTIES);
// -- ADAPTABLE PROVIDER -
const AdaptableAgGridContext = React.createContext(null);
const useAdaptableAgGridContext = () => {
    const context = React.useContext(AdaptableAgGridContext);
    if (!context) {
        throw new Error('AdaptableAgGridReact and AdaptableReact');
    }
    return context;
};
const AdaptableProvider = (props) => {
    const [currentTransition, setCurrentTransition] = React.useState(AdaptableAgGridStateTransitions.AG_GRID_EMIT_PROPS);
    const [agGridProps, setAgGridProps] = React.useState();
    const [gridOptions, setGridOptions] = React.useState(props.gridOptions);
    const [agGridApi, setAgGridApi] = React.useState();
    const adaptableAgGridReactRenderedRef = React.useRef(false);
    const gridApiPromiseResolve = React.useRef();
    const gridApiPromise = React.useMemo(() => {
        return new Promise((resolve) => {
            gridApiPromiseResolve.current = resolve;
        });
    }, []);
    const handleSetGridApi = React.useCallback((api) => {
        setAgGridApi(api);
        gridApiPromiseResolve.current(api);
    }, []);
    const renderAgGridFrameworkComponent = React.useCallback((gridOptions) => {
        setGridOptions(gridOptions);
        setCurrentTransition(AdaptableAgGridStateTransitions.INITIALIZE_AG_GRID);
        return gridApiPromise;
    }, [gridApiPromise]);
    // this is a check to make sure the user is rendering Adaptable.AgGridReact inside Adaptable.Provider
    // and not AgGridReact directly
    // we're waiting 1s for this to happen
    React.useEffect(() => {
        const timeoutId = setTimeout(() => {
            // this ref is updated in setAgridProps function in the context
            // which is called by the Adaptable.AgGridReact component on mount
            // so if this has not been updated, it means the user is not rendering Adaptable.AgGridReact
            // so we show an error in the console and link to the docs
            if (!adaptableAgGridReactRenderedRef.current) {
                console.error('You are not rendering <Adaptable.AgGridReact /> inside <Adaptable.Provider />.');
                console.error('You are probably rendering <AgGridReact /> directly. Please use <Adaptable.AgGridReact /> instead.');
                console.error('For more details, please see https://docs.adaptabletools.com/guide/react-integration');
            }
        }, 1000);
        return () => {
            clearTimeout(timeoutId);
        };
    }, []);
    return (React.createElement(AdaptableAgGridContext.Provider, { value: {
            transition: currentTransition,
            setTransition: setCurrentTransition,
            modules: props.modules,
            gridOptions,
            setGridOptions,
            agGridApi,
            setAgGridApi: handleSetGridApi,
            agGridProps,
            setAgGridProps: (agGridProps) => {
                adaptableAgGridReactRenderedRef.current = true;
                setAgGridProps(agGridProps);
                // get props and add the to gridOptions
                const newGridOptions = Object.assign(Object.assign({}, gridOptions), agGridProps);
                // Check null or undefined
                if (newGridOptions.reactiveCustomComponents == null) {
                    newGridOptions.reactiveCustomComponents = true;
                }
                setGridOptions(newGridOptions);
                // Adaptable has everything it needs to initialize
                setCurrentTransition(AdaptableAgGridStateTransitions.INITIALIZE_ADAPTABLE);
            },
            adaptableProps: Object.assign(Object.assign({}, props), { renderAgGridFrameworkComponent }),
        } }, props.children));
};
// -- ADAPTABLE UI WRAPPER -
const AdaptableUI = (props) => {
    const { adaptableProps, modules, gridOptions, transition } = useAdaptableAgGridContext();
    if (transition === AdaptableAgGridStateTransitions.AG_GRID_EMIT_PROPS) {
        return null;
    }
    return (React.createElement(AdaptableReactComponent
    // This needs to be first, so gridOptions is the one passed and not from props
    , Object.assign({}, adaptableProps, { style: props.style, className: props.className, gridOptions: gridOptions, modules: modules })));
};
const AdaptableAgGridReact = (props) => {
    const agGridRef = React.useRef(null);
    const [isAgGridReady, setAgGridIsReady] = React.useState(false);
    const { modules, gridOptions, setAgGridApi, transition, setAgGridProps, agGridApi } = useAdaptableAgGridContext();
    const propsRef = React.useRef(props);
    React.useEffect(() => {
        const prevProps = propsRef.current;
        if (isAgGridReady && agGridApi && prevProps !== props) {
            const changedProps = diff(prevProps, props);
            for (const changedProp of changedProps) {
                if (agGridPropertySet.has(changedProp)) {
                    agGridApi.setGridOption(changedProp, props[changedProp]);
                }
            }
        }
    }, [props, agGridApi]);
    const LIST_OF_PROPS_NOT_ON_GRID_OPTIONS = [
        'rowData',
        'containerStyle',
        'className',
        'setGridApi',
        'componentWrappingElement',
        'maxComponentCreationTimeMs',
        'children',
    ];
    React.useEffect(() => {
        const agGridProps = Object.keys(props).reduce((acc, key) => {
            if (LIST_OF_PROPS_NOT_ON_GRID_OPTIONS.includes(key)) {
                return acc;
            }
            return Object.assign(Object.assign({}, acc), { [key]: props[key] });
        }, {});
        setAgGridProps(agGridProps);
    }, []);
    const agGridPassThroughProps = React.useMemo(() => {
        return LIST_OF_PROPS_NOT_ON_GRID_OPTIONS.reduce((acc, key) => {
            if (key in props) {
                // @ts-ignore
                acc[key] = props[key];
            }
            return acc;
        }, {});
    }, [props]);
    if (transition !== AdaptableAgGridStateTransitions.INITIALIZE_AG_GRID) {
        return null;
    }
    return (React.createElement(AgGridReact, Object.assign({ ref: agGridRef }, agGridPassThroughProps, { modules: modules, onGridReady: (event) => {
            setAgGridApi(event.api);
            if (props.onGridReady) {
                props.onGridReady(event);
            }
            if (gridOptions.onGridReady) {
                gridOptions.onGridReady(event);
            }
            setAgGridIsReady(true);
        }, gridOptions: gridOptions })));
};
export const Adaptable = {
    Provider: AdaptableProvider,
    UI: AdaptableUI,
    AgGridReact: AdaptableAgGridReact,
};
